import React, { useState, useEffect } from 'react';
import { User, Menu, Sparkles } from 'lucide-react';
import { ABLE_COLORS } from '../../constants';
import MabelLogo from '../MabelLogo';
import ProfileMenu from './ProfileMenu';
import CompactStateBar from './CompactStateBar';

// localStorage key for tracking visits
const LAST_VISIT_KEY = 'mabel_last_visit';
const LAST_COMPLETED_COUNT_KEY = 'mabel_last_completed_count';

/**
 * Get time-based greeting
 */
function getTimeBasedGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  if (hour < 21) return 'Good evening';
  return 'Hey there';
}

/**
 * Get welcome back info based on last visit
 * Returns { isWelcomeBack, hoursSince, message } or null
 */
function getWelcomeBackInfo(completedSinceLastVisit) {
  const lastVisit = localStorage.getItem(LAST_VISIT_KEY);
  
  if (!lastVisit) {
    return null;
  }
  
  const lastVisitDate = new Date(parseInt(lastVisit, 10));
  const now = new Date();
  const hoursSince = (now - lastVisitDate) / (1000 * 60 * 60);
  
  // Only show welcome back after 8+ hours (roughly a sleep cycle or work day)
  if (hoursSince < 8) {
    return null;
  }
  
  // Build welcome back message
  if (hoursSince >= 72) {
    // 3+ days
    return {
      isWelcomeBack: true,
      hoursSince,
      message: "Hey, it's been a few days. No pressure - let's see where you're at."
    };
  } else if (hoursSince >= 24) {
    // 1-3 days
    if (completedSinceLastVisit > 0) {
      return {
        isWelcomeBack: true,
        hoursSince,
        message: `Welcome back! You completed ${completedSinceLastVisit} task${completedSinceLastVisit === 1 ? '' : 's'} last time.`
      };
    }
    return {
      isWelcomeBack: true,
      hoursSince,
      message: "Welcome back! Ready to pick up where you left off?"
    };
  } else {
    // 8-24 hours (morning after, etc.)
    if (completedSinceLastVisit > 0) {
      return {
        isWelcomeBack: true,
        hoursSince,
        message: `Nice work last session - you finished ${completedSinceLastVisit} task${completedSinceLastVisit === 1 ? '' : 's'}.`
      };
    }
    return null;
  }
}

/**
 * Get contextual message based on state
 * Uses: mood (Rough/Meh/Okay/Good/Great), energy (Low/Okay/High), intents (array), location
 */
function getContextualMessage(state, welcomeBackInfo) {
  // If we have a welcome back message, use that
  if (welcomeBackInfo?.isWelcomeBack) {
    return welcomeBackInfo.message;
  }
  
  const { mood, energy, intents } = state || {};
  const hasIntent = intents && intents.length > 0;
  const firstIntent = hasIntent ? intents[0] : null;

  // Empty state - no check-in yet
  if (!mood && !energy && !hasIntent) {
    return 'What would you like to do today?';
  }

  // Intent-specific phrases
  const intentPhrases = {
    'Creative': 'creative',
    'Mental': 'thinking',
    'Emotional': 'processing',
    'Social': 'connecting',
    'Physical': 'moving',
    'Rest': 'resting'
  };

  // Mood-based messages (prioritize mood for tone)
  if (mood === 'Rough') {
    return hasIntent 
      ? `Be gentle with yourself. Small ${intentPhrases[firstIntent] || ''} steps are okay.`
      : 'Be gentle with yourself today.';
  }

  if (mood === 'Meh') {
    return hasIntent
      ? `Small ${intentPhrases[firstIntent] || ''} steps are fine.`
      : 'Small steps are fine today.';
  }

  if (mood === 'Great' || (mood === 'Good' && energy === 'High')) {
    return hasIntent
      ? `Great energy for ${intentPhrases[firstIntent] || 'getting things done'}!`
      : "You've got great energy today!";
  }

  if (mood === 'Good') {
    return hasIntent
      ? `Ready for some ${intentPhrases[firstIntent] || 'tasks'}.`
      : 'What would help right now?';
  }

  // Okay mood or just intent/energy set
  if (hasIntent) {
    const intentMessages = {
      'Creative': 'Ready for creative work.',
      'Mental': "Let's tackle some thinking.",
      'Emotional': 'Taking time to process.',
      'Social': 'People time.',
      'Physical': 'Time to move.',
      'Rest': 'Rest is productive too.'
    };
    return intentMessages[firstIntent] || 'What would help right now?';
  }

  return 'What would help right now?';
}

/**
 * Format date based on user preference
 * @param {Date} date - The date to format
 * @param {string} format - 'DMY' | 'MDY' | 'ISO'
 */
function formatDate(date, format = 'DMY') {
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();
  
  switch (format) {
    case 'MDY':
      return `${month} ${day}, ${year}`;
    case 'ISO':
      return date.toISOString().split('T')[0];
    case 'DMY':
    default:
      return `${day} ${month} ${year}`;
  }
}

/**
 * MabelHeader - Logo and greeting that always shows at top of Hub
 */
export default function MabelHeader({ 
  user, 
  currentState, 
  onStateChange,
  displayName, 
  dateFormat = 'DMY',
  onDisplayNameChange,
  onDateFormatChange,
  recentCompletedCount = 0,
  isMobile = false,
  onOpenSidebar,
  onOpenChat,
  showToast
}) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [welcomeBackInfo, setWelcomeBackInfo] = useState(null);
  const [hasCheckedWelcome, setHasCheckedWelcome] = useState(false);
  
  const greeting = getTimeBasedGreeting();
  // Use custom display name if set, otherwise fall back to Kinde name
  const firstName = displayName || user?.given_name || 'there';
  
  // Check for welcome back on mount (only once)
  useEffect(() => {
    if (!hasCheckedWelcome) {
      // Get last session's completed count
      const lastCompletedCount = parseInt(localStorage.getItem(LAST_COMPLETED_COUNT_KEY) || '0', 10);
      const info = getWelcomeBackInfo(lastCompletedCount);
      setWelcomeBackInfo(info);
      setHasCheckedWelcome(true);
      
      // Update last visit timestamp
      localStorage.setItem(LAST_VISIT_KEY, Date.now().toString());
      // Reset completed count for this session
      localStorage.setItem(LAST_COMPLETED_COUNT_KEY, '0');
    }
  }, [hasCheckedWelcome]);
  
  // Track completed tasks for next session
  useEffect(() => {
    if (recentCompletedCount > 0) {
      localStorage.setItem(LAST_COMPLETED_COUNT_KEY, recentCompletedCount.toString());
    }
  }, [recentCompletedCount]);
  
  // Clear welcome back message after user interacts (checks in state)
  useEffect(() => {
    if (welcomeBackInfo && currentState?.mood) {
      // User has checked in, clear the welcome back message
      setWelcomeBackInfo(null);
    }
  }, [currentState?.mood, welcomeBackInfo]);
  
  const contextMessage = getContextualMessage(currentState, welcomeBackInfo);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div 
      className="mabel-header"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        boxShadow: ABLE_COLORS.cardShadow,
        marginBottom: '16px'
      }}
    >
      <style>{`
        .mabel-header {
          padding: 16px 20px;
        }
        @media (max-width: 400px) {
          .mabel-header {
            padding: 14px 12px;
            gap: 10px;
          }
        }
      `}</style>
      {/* Mobile hamburger menu */}
      {isMobile && onOpenSidebar && (
        <button
          onClick={onOpenSidebar}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '-6px',
          }}
          title="Open menu"
        >
          <Menu size={22} color={ABLE_COLORS.textSecondary} />
        </button>
      )}
      <MabelLogo size={40} withBackground />
      <div style={{ flex: 1, minWidth: 0 }}>
        <h1 className="mabel-greeting" style={{
          margin: 0,
          fontWeight: '600',
          color: ABLE_COLORS.textPrimary
        }}>
          {greeting}, {firstName}.
        </h1>
        <p className="mabel-subtext" style={{
          margin: '2px 0 0 0',
          color: ABLE_COLORS.textSecondary,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {contextMessage}
        </p>
      </div>
      <style>{`
        .mabel-greeting {
          font-size: 22px;
        }
        .mabel-subtext {
          font-size: 14px;
        }
        @media (max-width: 400px) {
          .mabel-greeting {
            font-size: 18px;
          }
          .mabel-subtext {
            font-size: 13px;
          }
        }
      `}</style>

      {/* Compact State Check-in */}
      {onStateChange && (
        <CompactStateBar
          currentState={currentState}
          onStateChange={onStateChange}
          onToast={showToast}
        />
      )}

      {/* Date and Time */}
      <div style={{
        textAlign: 'right',
        fontSize: '13px',
        color: ABLE_COLORS.textMuted,
        flexShrink: 0
      }}>
        <div style={{ fontWeight: '600' }}>
          {formatTime(currentTime)}
        </div>
        <div className="header-date" style={{ fontSize: '12px' }}>
          {formatDate(currentTime, dateFormat)}
        </div>
      </div>
      <style>{`
        @media (max-width: 380px) {
          .header-date { display: none; }
        }
      `}</style>

      {/* Ask Mabel button */}
      {onOpenChat && (
        <button
          onClick={onOpenChat}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${ABLE_COLORS.primary} 0%, ${ABLE_COLORS.secondary} 100%)`,
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          title="Ask Mabel"
        >
          <Sparkles size={18} color="white" />
        </button>
      )}

      {/* Profile button */}
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setShowProfileMenu(!showProfileMenu)}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: showProfileMenu ? `${ABLE_COLORS.primary}20` : ABLE_COLORS.bgSubtle,
            border: `1px solid ${showProfileMenu ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s ease'
          }}
          title="Account settings"
        >
          <User size={18} color={showProfileMenu ? ABLE_COLORS.primary : ABLE_COLORS.textSecondary} />
        </button>

        {showProfileMenu && (
          <ProfileMenu 
            user={user} 
            onClose={() => setShowProfileMenu(false)}
            onDisplayNameChange={onDisplayNameChange}
            dateFormat={dateFormat}
            onDateFormatChange={onDateFormatChange}
          />
        )}
      </div>
    </div>
  );
}
